import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, ClipboardList, FileText, Loader2, CheckCircle2, ExternalLink, X, Upload, Image as ImageIcon, Pencil, Mic, Video, Link2, Check } from 'lucide-react'
import { assignmentApi, courseApi, type Assignment, type Submission, type ToGradeItem, type RubricItem } from '../api/lessonBuilderApi'
import { fmtDue } from '../types/CourseDoc'
import KaTeXRenderer from '../../../components/common/KaTeXRenderer'
import AnnotateModal from '../components/AnnotateModal'
import AnnotatedImage from '../components/AnnotatedImage'
import { openExternalClick, normalizeUploadUrl } from '../lib/openExternal'

const isPdf = (u?: string) => !!u && /\.pdf($|\?)/i.test(u)
const isDirectVideo = (u?: string) => !!u && /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(u)

// Trang GV: quản lý bài tập tự luận + CHẤM TAY (điểm + nhận xét). Điểm vào Sổ điểm.
const AssignmentsPage: React.FC = () => {
  const [list, setList] = useState<Assignment[] | null>(null)
  const [courses, setCourses] = useState<{ id: string; title: string }[]>([])
  const [sel, setSel] = useState<Assignment | null>(null)
  const [subs, setSubs] = useState<Submission[] | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [pending, setPending] = useState<ToGradeItem[]>([])
  const [params] = useSearchParams()
  const courseId = params.get('course') || ''   // lọc bài tập theo KHOÁ (vào từ trang soạn khoá)

  const loadPending = () => assignmentApi.toGrade().then((d) => setPending(d.items)).catch(() => {})
  const reload = () => { assignmentApi.listMine().then(setList); loadPending() }
  useEffect(() => {
    reload()
    courseApi.list().then((cs) => setCourses(cs.map((c) => ({ id: c.id, title: c.title })))).catch(() => {})
  }, [])

  const openGrade = async (a: Assignment) => {
    setSel(a); setSubs(null)
    const r = await assignmentApi.submissions(a.id)
    setSubs(r.submissions)
  }
  const openGradeById = (aid: string) => { const a = (list || []).find((x) => x.id === aid); if (a) openGrade(a) }

  const remove = async (a: Assignment) => {
    if (!window.confirm(`Xoá bài tập "${a.title}"?`)) return
    await assignmentApi.remove(a.id)
    if (sel?.id === a.id) { setSel(null); setSubs(null) }
    reload()
  }

  const [copiedId, setCopiedId] = useState('')
  const copyLink = async (a: Assignment) => {
    const url = `${window.location.origin}/bai-tap/${a.slug || a.id}`   // link NGẮN GỌN (slug thay UUID)
    try { await navigator.clipboard.writeText(url) } catch { window.prompt('Sao chép liên kết:', url) }
    setCopiedId(a.id); setTimeout(() => setCopiedId(''), 1500)
  }

  // Lọc theo khoá (nếu vào từ trang soạn khoá): danh sách bài + hộp "Cần chấm" chỉ của khoá đó.
  const shown = courseId ? (list || []).filter((a) => a.course_id === courseId) : (list || [])
  const courseAids = new Set(shown.map((a) => a.id))
  const pendingShown = courseId ? pending.filter((p) => courseAids.has(p.assignment_id)) : pending
  const courseTitle = courses.find((c) => c.id === courseId)?.title

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-5">
          <Link to="/teacher/khoa-hoc" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
            <ArrowLeft size={16} /> Khoá học
          </Link>
          <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
            <ClipboardList className="text-blue-600" size={24} /> Bài tập tự luận
          </h1>
          <button onClick={() => setShowForm(true)}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">
            <Plus size={18} /> Tạo bài tập
          </button>
        </div>

        {/* Ngữ cảnh KHOÁ (khi vào từ trang soạn khoá) */}
        {courseId && (
          <div className="mb-4 flex items-center gap-2 flex-wrap rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm">
            <ClipboardList size={16} className="text-blue-600" />
            <span className="font-bold text-blue-800">Bài tập của khoá: {courseTitle || '…'}</span>
            <Link to="/bai-tap" className="ml-auto text-blue-600 font-semibold hover:underline">Xem tất cả bài tập →</Link>
          </div>
        )}

        {/* Hộp CẦN CHẤM — gom bài chờ chấm (theo khoá nếu đang lọc) */}
        {pendingShown.length > 0 && (
          <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="font-black text-amber-800 mb-2 flex items-center gap-2">
              <ClipboardList size={18} /> Cần chấm ({pendingShown.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {pendingShown.slice(0, 12).map((p) => (
                <button key={p.submission_id} onClick={() => openGradeById(p.assignment_id)}
                  className="text-left px-3 py-2 rounded-xl bg-white border border-amber-200 hover:border-amber-400 text-sm">
                  <span className="font-bold text-slate-800">{p.student_name || p.student_email || 'Học viên'}</span>
                  <span className="block text-[11px] text-amber-700 truncate max-w-[200px]">{p.assignment_title}</span>
                </button>
              ))}
              {pendingShown.length > 12 && <span className="self-center text-xs text-amber-700">+{pendingShown.length - 12} bài khác…</span>}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-[360px_1fr] gap-5">
          {/* Danh sách bài tập */}
          <div className="space-y-2">
            {list === null ? (
              <div className="py-10 text-center text-gray-300"><Loader2 className="inline animate-spin" /></div>
            ) : shown.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-400 text-sm">
                {courseId ? 'Khoá này chưa có bài tập tự luận. Bấm "Tạo bài tập" (sẽ tự gắn vào khoá).' : 'Chưa có bài tập. Bấm "Tạo bài tập" để giao bài tự luận cho học viên.'}
              </div>
            ) : shown.map((a) => (
              <div key={a.id}
                className={`bg-white rounded-xl border p-4 cursor-pointer transition ${sel?.id === a.id ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-100 hover:border-blue-200'}`}
                onClick={() => openGrade(a)}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-slate-800 text-sm flex-1">{a.title}</h3>
                  <button onClick={(e) => { e.stopPropagation(); copyLink(a) }} title="Sao chép liên kết ngắn để gửi học viên"
                    className={`shrink-0 ${copiedId === a.id ? 'text-emerald-600' : 'text-slate-400 hover:text-blue-600'}`}>
                    {copiedId === a.id ? <Check size={15} /> : <Link2 size={15} />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); remove(a) }} className="text-rose-400 hover:text-rose-600 shrink-0"><Trash2 size={15} /></button>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500">
                  <span>Thang {a.max_score}đ</span>
                  {a.due_date && <span>· Hạn {fmtDue(a.due_date)}</span>}
                </div>
                <div className="flex items-center gap-2 mt-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 font-bold">{a.submissionCount || 0} bài nộp</span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold">{a.gradedCount || 0} đã chấm</span>
                </div>
              </div>
            ))}
          </div>

          {/* Khu chấm bài */}
          <div>
            {!sel ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-400">
                Chọn một bài tập bên trái để xem &amp; chấm bài nộp.
              </div>
            ) : (
              <GradePanel assignment={sel} subs={subs} onGraded={(s) => {
                setSubs((prev) => (prev || []).map((x) => x.id === s.id ? s : x))
                reload()
              }} />
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <CreateForm courses={courses} defaultCourseId={courseId} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reload() }} />
      )}
    </div>
  )
}

// Khu chấm bài nộp của 1 bài tập
const GradePanel: React.FC<{ assignment: Assignment; subs: Submission[] | null; onGraded: (s: Submission) => void }> = ({ assignment, subs, onGraded }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      <div className="p-5 border-b border-gray-100">
        <h2 className="font-black text-slate-900">{assignment.title}</h2>
        {assignment.prompt && <div className="text-sm text-slate-500 mt-1"><KaTeXRenderer content={assignment.prompt} /></div>}
      </div>
      {subs === null ? (
        <div className="py-12 text-center text-gray-300"><Loader2 className="inline animate-spin" /></div>
      ) : subs.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">Chưa có bài nộp nào.</div>
      ) : (
        <div className="divide-y divide-gray-100">
          {subs.map((s) => <SubmissionRow key={s.id} sub={s} assignment={assignment} onGraded={onGraded} />)}
        </div>
      )}
    </div>
  )
}

const SubmissionRow: React.FC<{ sub: Submission; assignment: Assignment; onGraded: (s: Submission) => void }> = ({ sub, assignment, onGraded }) => {
  const max = assignment.max_score
  const rubric = assignment.rubric || []
  const [score, setScore] = useState<string>(sub.score != null ? String(sub.score) : '')
  const [items, setItems] = useState<number[]>(() => rubric.map((_, i) => sub.itemScores?.[i] ?? 0))
  const [fb, setFb] = useState(sub.feedback || '')
  const [videoUrl, setVideoUrl] = useState(sub.feedback_video_url || '')
  const [busy, setBusy] = useState(false)
  const [uploadingAudio, setUploadingAudio] = useState(false)
  const [annotateUrl, setAnnotateUrl] = useState<string | null>(null)
  const subFiles = (sub.files && sub.files.length ? sub.files : (sub.file_url ? [sub.file_url] : [])).map(normalizeUploadUrl)
  const annos = sub.annotations || []
  const feedbackAudioUrl = sub.feedback_audio_url || ''
  const total = rubric.length ? Math.round(items.reduce((a, b) => a + (b || 0), 0) * 100) / 100 : 0

  useEffect(() => {
    setScore(sub.score != null ? String(sub.score) : '')
    setItems(rubric.map((_, i) => sub.itemScores?.[i] ?? 0))
    setFb(sub.feedback || '')
    setVideoUrl(sub.feedback_video_url || '')
  }, [sub, rubric])

  // Gom rubric phẳng theo Câu (giữ thứ tự). Mục không có cau → nhóm rỗng (hiện phẳng như cũ).
  const groups: { cau: string; idxs: number[] }[] = []
  rubric.forEach((r, i) => {
    const key = r.cau || ''
    const last = groups[groups.length - 1]
    if (last && last.cau === key) last.idxs.push(i)
    else groups.push({ cau: key, idxs: [i] })
  })

  const save = async () => {
    setBusy(true)
    try {
      let g: Submission
      if (rubric.length) {
        g = await assignmentApi.grade(sub.id, { itemScores: items, feedback: fb, feedbackVideoUrl: videoUrl })
      } else {
        const n = Number(score)
        if (isNaN(n) || n < 0 || n > max) { alert(`Điểm phải trong khoảng 0–${max}.`); setBusy(false); return }
        g = await assignmentApi.grade(sub.id, { score: n, feedback: fb, feedbackVideoUrl: videoUrl })
      }
      onGraded(g)
    } catch {
      alert('Lưu điểm chưa thành công, thử lại.')
    } finally { setBusy(false) }
  }

  const delAnno = async (url: string) => {
    if (!window.confirm('Gỡ ảnh ghi chú này?')) return
    try { onGraded(await assignmentApi.deleteAnnotation(sub.id, url)) } catch { alert('Gỡ ghi chú thất bại.') }
  }

  const uploadFeedbackAudio = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = ''
    if (!f) return
    setUploadingAudio(true)
    try {
      onGraded(await assignmentApi.uploadFeedbackAudio(sub.id, f))
    } catch {
      alert('Tải audio phản hồi chưa thành công.')
    } finally {
      setUploadingAudio(false)
    }
  }

  const removeFeedbackAudio = async () => {
    if (!window.confirm('Gỡ audio phản hồi này?')) return
    try {
      onGraded(await assignmentApi.deleteFeedbackAudio(sub.id))
    } catch {
      alert('Gỡ audio phản hồi thất bại.')
    }
  }

  return (
    <div className="p-5">
      {/* Học sinh + trạng thái + email */}
      <div className="flex items-center gap-2 mb-3">
        <span className="font-bold text-slate-800">{sub.student_name || sub.student_email || 'Học viên'}</span>
        {sub.status === 'graded'
          ? <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600"><CheckCircle2 size={13} /> Đã chấm {sub.score}đ</span>
          : <span className="text-[11px] font-bold text-amber-600">● Chờ chấm</span>}
        {sub.student_email && <span className="text-[11px] text-gray-400 ml-auto truncate max-w-[45%]">{sub.student_email}</span>}
      </div>

      {/* BÀI LÀM CỦA HỌC VIÊN — nội dung + file + nút Chấm bài */}
      {(sub.content || subFiles.length > 0) && (
        <div className="mb-3">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Bài làm của học viên</div>
          {sub.content && <div className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap mb-2">{sub.content}</div>}
          {subFiles.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              {subFiles.map((u, i) => (
                <div key={i} className="flex items-center gap-2">
                  {!isPdf(u) && (
                    <button onClick={() => setAnnotateUrl(u)} title="Chấm bài trên ảnh này">
                      <img src={u} alt="Lời giải" className="h-24 rounded-lg border border-gray-200 hover:ring-2 hover:ring-emerald-300" />
                    </button>
                  )}
                  <button onClick={() => setAnnotateUrl(u)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700">
                    <Pencil size={14} /> Chấm bài{subFiles.length > 1 ? ' ' + (i + 1) : ''}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* BẢN ĐÃ CHẤM (học sinh xem được) */}
      {annos.length > 0 && (
        <div className="mb-3">
          <div className="text-[11px] font-bold text-amber-700 uppercase tracking-wide mb-1.5">Bản đã chấm · học sinh xem được</div>
          <div className="flex flex-wrap gap-2">
            {annos.map((u, i) => (
              <div key={i} className="relative">
                <AnnotatedImage url={u} alt={`Bài đã chấm ${i + 1}`} thumbClass="h-24 rounded-lg border border-amber-200" downloadName={`bai-cham-${i + 1}.png`} />
                <button onClick={() => delAnno(u)} title="Gỡ" className="absolute -top-2 -right-2 bg-white rounded-full p-0.5 shadow text-rose-500 hover:text-rose-700"><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CHẤM ĐIỂM & PHẢN HỒI — gom 1 khối, nút Lưu ở cuối */}
      <div className="rounded-xl border border-gray-200 p-3.5 space-y-3">
        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Chấm điểm &amp; phản hồi</div>

        {/* Rubric Câu → ý (tự cộng) */}
        {rubric.length > 0 && (
          <div className="space-y-2">
            {groups.map((g, gi) => {
              const gsub = Math.round(g.idxs.reduce((a, i) => a + (items[i] || 0), 0) * 100) / 100
              const gmax = Math.round(g.idxs.reduce((a, i) => a + (rubric[i].max || 0), 0) * 100) / 100
              return (
                <div key={gi} className={g.cau ? 'rounded-md bg-gray-50/70 border border-gray-100 p-2' : ''}>
                  {g.cau && (
                    <div className="flex items-center justify-between text-xs font-black text-slate-700 mb-1">
                      <span>{g.cau}</span><span className="text-emerald-700">{gsub}/{gmax}đ</span>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {g.idxs.map((i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="flex-1 text-sm text-slate-700">{rubric[i].label}</span>
                        <input type="number" min={0} max={rubric[i].max} step="0.25" value={items[i] ?? 0}
                          onChange={(e) => setItems((p) => p.map((v, k) => k === i ? Math.max(0, Math.min(Number(e.target.value) || 0, rubric[i].max)) : v))}
                          className="w-20 p-1.5 border border-gray-200 rounded text-sm text-center outline-none focus:ring-2 focus:ring-blue-200" />
                        <span className="text-xs text-gray-400 w-10">/{rubric[i].max}đ</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
            <div className="flex items-center justify-end pt-1 border-t border-gray-100 text-sm font-black text-emerald-700">
              Tổng: {total}/{max}đ
            </div>
          </div>
        )}

        {/* Điểm (khi không có rubric) + Nhận xét */}
        <div className="flex flex-wrap items-end gap-3">
          {rubric.length === 0 && (
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase mb-0.5">Điểm (/{max})</label>
              <input type="number" min={0} max={max} step="0.25" value={score} onChange={(e) => setScore(e.target.value)}
                className="w-24 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
          )}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-0.5">Nhận xét</label>
            <input value={fb} onChange={(e) => setFb(e.target.value)} placeholder="Nhận xét cho học viên…"
              className="w-full p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
        </div>

        {/* Phản hồi thêm (audio + video) — gọn 1 hàng */}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
          <span className="text-[11px] font-bold text-gray-400 uppercase">Phản hồi thêm</span>
          <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-sky-50 border border-sky-200 text-sky-700 text-sm font-bold hover:bg-sky-100 cursor-pointer">
            {uploadingAudio ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />} Audio
            <input type="file" accept="audio/*" className="hidden" onChange={uploadFeedbackAudio} disabled={uploadingAudio} />
          </label>
          {feedbackAudioUrl && (
            <>
              <audio controls src={feedbackAudioUrl} className="h-8 max-w-[220px]" />
              <button onClick={removeFeedbackAudio} title="Gỡ audio" className="text-rose-400 hover:text-rose-600"><X size={14} /></button>
            </>
          )}
          <span className="inline-flex items-center gap-1.5 flex-1 min-w-[220px]">
            <Video size={15} className="text-sky-600 shrink-0" />
            <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="Dán link video phản hồi (YouTube, Loom, MP4…)"
              className="flex-1 min-w-0 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200" />
          </span>
          {sub.feedback_video_url && (
            <a href={sub.feedback_video_url} onClick={openExternalClick(sub.feedback_video_url)} title="Mở video hiện tại" className="inline-flex items-center gap-1 text-sm font-bold text-sky-700 hover:text-sky-900">
              {isDirectVideo(sub.feedback_video_url) ? <Video size={14} /> : <ExternalLink size={14} />} Mở
            </a>
          )}
        </div>

        {/* Lưu */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <span className="text-[11px] text-gray-400 mr-auto">Nhận xét + điểm + ảnh chấm sẽ gửi tới học sinh.</span>
          <button onClick={save} disabled={busy}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Lưu điểm &amp; gửi
          </button>
        </div>
      </div>

      {annotateUrl && (
        <AnnotateModal fileUrl={annotateUrl} submissionId={sub.id}
          onClose={() => setAnnotateUrl(null)} onSaved={(s) => onGraded(s)} />
      )}
    </div>
  )
}

// Form tạo bài tập
const CreateForm: React.FC<{ courses: { id: string; title: string }[]; defaultCourseId?: string; onClose: () => void; onSaved: () => void }> = ({ courses, defaultCourseId, onClose, onSaved }) => {
  const [title, setTitle] = useState('')
  const [prompt, setPrompt] = useState('')
  const [maxScore, setMaxScore] = useState('10')
  const [due, setDue] = useState('')
  const [courseId, setCourseId] = useState(defaultCourseId || '')
  const [caus, setCaus] = useState<{ items: { y: string; max: number }[] }[]>([])
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const yLetter = (i: number) => String.fromCharCode(97 + i)   // a, b, c…
  const hasRubric = caus.some((c) => c.items.length > 0)
  const rubricTotal = Math.round(caus.reduce((a, c) => a + c.items.reduce((s, it) => s + (it.max || 0), 0), 0) * 100) / 100
  // Builder Câu → ý
  const addCau = () => setCaus((p) => [...p, { items: [{ y: '', max: 1 }] }])
  const delCau = (ci: number) => setCaus((p) => p.filter((_, k) => k !== ci))
  const addY = (ci: number) => setCaus((p) => p.map((c, k) => (k === ci ? { items: [...c.items, { y: '', max: 1 }] } : c)))
  const delY = (ci: number, yi: number) => setCaus((p) => p.map((c, k) => (k === ci ? { items: c.items.filter((_, j) => j !== yi) } : c)))
  const setY = (ci: number, yi: number, patch: Partial<{ y: string; max: number }>) =>
    setCaus((p) => p.map((c, k) => (k === ci ? { items: c.items.map((it, j) => (j === yi ? { ...it, ...patch } : it)) } : c)))

  // Nhận file (click hoặc kéo-thả): chỉ ảnh / PDF / Word
  const pickFile = (f?: File | null) => {
    if (!f) return
    const ok = f.type.startsWith('image/') || f.type === 'application/pdf' || /\.(png|jpe?g|webp|gif|pdf|docx?)$/i.test(f.name)
    if (!ok) { alert('Chỉ nhận ảnh, PDF hoặc Word (.doc/.docx).'); return }
    setFile(f)
  }

  const save = async () => {
    if (!title.trim()) { alert('Nhập tên bài tập.'); return }
    // Gom Câu → ý thành rubric phẳng (kèm khoá cau để chấm/gom theo Câu); tổng tự cộng.
    const flatRubric: RubricItem[] = []
    caus.forEach((c, ci) => {
      const cau = `Câu ${ci + 1}`
      c.items.forEach((it, yi) => {
        flatRubric.push({ cau, label: `${yLetter(yi)})${it.y.trim() ? ' ' + it.y.trim() : ''}`, max: Math.max(0, it.max || 0) })
      })
    })
    setBusy(true)
    try {
      const a = await assignmentApi.save({
        title: title.trim(), prompt, max_score: Number(maxScore) || 10,
        due_date: due || '', course_id: courseId || null, allow_file: true,
        rubric: flatRubric.length ? flatRubric : undefined,
      })
      if (file && a?.id) {
        try { await assignmentApi.uploadAttachment(a.id, file) }
        catch (e) {
          let why = ''
          try { const m = String((e as Error).message || ''); const j = m.slice(m.indexOf('{')); const d = JSON.parse(j); if (d?.detail) why = ' (' + d.detail + ')' } catch { /* giữ chung */ }
          alert('Đã tạo bài tập nhưng tải ĐỀ lên chưa thành công' + why + '. Mở bài tập để thử lại.')
        }
      }
      onSaved()
    } catch {
      alert('Tạo bài tập chưa thành công, thử lại.')
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2"><FileText size={20} className="text-blue-600" /> Tạo bài tập tự luận</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">Tên bài tập</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VD: Viết đoạn văn phân tích hiện tượng…"
              className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">Đề bài / Yêu cầu (gõ tay, có thể dùng $LaTeX$)</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="Mô tả yêu cầu… hoặc tải đề ảnh/PDF/Word bên dưới"
              className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 resize-y" />
          </div>
          {/* Tải ĐỀ: ảnh/PDF giữ nguyên; Word(.docx) MathType → LaTeX hiển thị */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">Tải đề (ảnh / PDF / Word có công thức)</label>
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]) }}
              className={`mt-1 flex flex-col items-center justify-center gap-1 px-3 py-4 border-2 border-dashed rounded-lg text-sm cursor-pointer transition-colors ${dragOver ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600 hover:border-blue-300 hover:bg-gray-50'}`}
            >
              <Upload size={20} className={dragOver ? 'text-blue-500' : 'text-gray-400'} />
              {file ? <span className="font-semibold text-blue-700">{file.name}</span>
                : <span><b className="text-blue-600">Kéo thả</b> file Word/ảnh/PDF vào đây, hoặc <b className="text-blue-600">bấm để chọn</b></span>}
              <input type="file" accept="image/*,application/pdf,.doc,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])} />
            </label>
            <p className="text-[11px] text-gray-400 mt-1">Ảnh/PDF: giữ nguyên cho HS xem & tải. Word có MathType: tự chuyển công thức sang LaTeX hiển thị.</p>
          </div>
          {/* Chấm điểm theo CÂU → Ý (a,b,c…) — điểm từng ý, tổng tự cộng */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">Chấm điểm theo Câu → ý (tuỳ chọn)</label>
            <p className="text-[11px] text-gray-400 mb-1.5">Bấm <b>“+ Câu”</b> để tạo Câu 1, 2, 3…; trong mỗi câu bấm <b>“+ ý”</b> để thêm ý a, b, c… và nhập điểm. Tổng tự cộng.</p>
            <div className="space-y-2">
              {caus.map((c, ci) => (
                <div key={ci} className="rounded-xl border border-gray-200 p-2.5 bg-gray-50/60">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-black text-slate-700">Câu {ci + 1}</span>
                    <button onClick={() => delCau(ci)} className="text-rose-400 hover:text-rose-600 inline-flex items-center gap-1 text-xs font-semibold"><Trash2 size={13} /> Xoá câu</button>
                  </div>
                  <div className="space-y-1.5">
                    {c.items.map((it, yi) => (
                      <div key={yi} className="flex items-center gap-2">
                        <span className="w-6 text-center font-bold text-slate-500">{yLetter(yi)})</span>
                        <input value={it.y} onChange={(e) => setY(ci, yi, { y: e.target.value })}
                          placeholder="Nội dung ý (tuỳ chọn)" className="flex-1 p-1.5 border border-gray-200 rounded text-sm outline-none focus:ring-2 focus:ring-blue-200" />
                        <input type="number" min={0} step="0.25" value={it.max} onChange={(e) => setY(ci, yi, { max: Math.max(0, Number(e.target.value) || 0) })}
                          className="w-16 p-1.5 border border-gray-200 rounded text-sm text-center outline-none focus:ring-2 focus:ring-blue-200" />
                        <span className="text-[11px] text-gray-400">đ</span>
                        <button onClick={() => delY(ci, yi)} className="p-1 text-rose-300 hover:text-rose-600"><Trash2 size={13} /></button>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => addY(ci)} className="mt-1.5 text-xs text-blue-600 font-semibold inline-flex items-center gap-1"><Plus size={12} /> Thêm ý</button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-2">
              <button onClick={addCau} className="text-sm text-blue-600 font-bold inline-flex items-center gap-1"><Plus size={14} /> Thêm Câu</button>
              {hasRubric && <span className="text-xs font-bold text-emerald-700">Tổng thang: {rubricTotal}đ (tự áp)</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Thang điểm {hasRubric && '(theo Câu/ý)'}</label>
              <input type="number" min={1} value={hasRubric ? rubricTotal : maxScore} disabled={hasRubric} onChange={(e) => setMaxScore(e.target.value)}
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Hạn nộp (tuỳ chọn)</label>
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">Gắn vào khoá học (để học viên thấy)</label>
            <select value={courseId} onChange={(e) => setCourseId(e.target.value)}
              className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white">
              <option value="">— Không gắn (chia sẻ qua đường link) —</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100">Huỷ</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Đang lưu…' : 'Tạo bài tập'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AssignmentsPage
